import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import type { Observable } from 'rxjs';

import {
  AvailableLanguages,
  I18nPlatformService,
  LanguageService,
  PlatformService,
} from '@evc/platform';
import {
  EvcInputOption,
  IconComponent,
  InputValueTypes,
  safeUri,
  SelectComponent,
  ThemeService,
  ToastComponent,
  ToastService,
  WebComponentsConfigService,
} from '@evc/web-components';
import { SharedConfigService } from '@shared/services/config/config.service';
import { I18nService, TranslatePipe } from '@shared/services/i18n/i18n.service';
import { getLocation } from '@shared/utils/window.helper';

import type { AppConfigService } from '@app/types/config.type';

@Component({
  selector: 'evc-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    IconComponent,
    RouterOutlet,
    ToastComponent,
    SelectComponent,
  ],
})
export class AppComponent implements OnInit {
  readonly #configService: AppConfigService = inject(SharedConfigService);
  readonly #platformService = inject(PlatformService);
  readonly #i18nService = inject(I18nService);
  readonly #i18nPlatformService = inject(I18nPlatformService);
  readonly #languageService = inject(LanguageService);
  readonly #themeService = inject(ThemeService);
  readonly #toastService = inject(ToastService);
  #webComponentsConfigService = inject(WebComponentsConfigService);

  #languages = this.#configService.get('i18n').platformLanguages;
  protected readonly languagesOptions = computed<EvcInputOption<AvailableLanguages>[]>(() => {
    return this.#languages.map((lang) => ({
      label: this.#i18nPlatformService.t(`menus.language.${lang}`),
      value: lang,
    }));
  });
  protected readonly currentLanguage = this.#languageService.lang;
  protected setLanguage(lang: InputValueTypes) {
    this.#languageService.setLanguage(lang as AvailableLanguages);
  }

  toast = this.#toastService.current;
  title?: string | Observable<string> = this.#configService.get('app').title;
  ready = this.#platformService.ready;

  ngOnInit(): void {
    this.#themeService.setTheme('light');
    this.#setRedirectConfig();
    this.#platformService.init(this.#i18nService);
  }

  /**
   * remember where to redirect on quit this flow
   * - ?redirect_uri may link to app if user have other orgs
   * - or redirect to public website if user have no active org
   *
   * use sessionStorage because may loginRedirect just before using flow
   */
  #setRedirectConfig() {
    const url = new URL(getLocation().href);

    const fromUrlParams = safeUri(url.searchParams.get('redirect_uri'));
    const fromSessionStorage = safeUri(sessionStorage.getItem('redirect_uri'));
    const defaultRedirectFailure = this.#configService.get('uris').admin;

    if (fromUrlParams) {
      sessionStorage.setItem('redirect_uri', fromUrlParams);
    } else {
      sessionStorage.removeItem('redirect_uri');
    }

    const redirectUri = fromUrlParams || fromSessionStorage || defaultRedirectFailure;

    this.#configService.set('redirectUri', redirectUri);

    const backRedirectUri = safeUri(url.searchParams.get('back_redirect_uri'));
    if (backRedirectUri) this.#configService.set('backRedirectUri', backRedirectUri);
  }

  onBackButtonClicked(): void {
    const backRedirectUri = safeUri(this.#configService.get('backRedirectUri'));
    const { homepage: defaultRedirectUri } = this.#configService.get('uris');

    this.#webComponentsConfigService.openLink(backRedirectUri || defaultRedirectUri, false);
  }
}
